import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input, Textarea } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Spinner } from '../../components/ui/Spinner';
import { DecimalInput } from '../../components/ui/DecimalInput';
import { fmt } from '../../lib/formatters';
import { useVolver } from '../../lib/useVolver';
import { openCotizMailto } from '../../lib/cotizMailto';
import { confirmar } from '../../lib/toast';

// ─── Estados de cotización ──────────────────────────────────────────
// Sincronizado con migración 038. Cambios aquí sin tocar el CHECK SQL
// hacen que el backend rechace el valor: por diseño.
export const COTIZ_ESTADOS = {
  PDTE_DE_ENVIAR: { label: 'Pdte. de enviar', color: '#64748b' },
  ENVIADA_PDTE_DE_RESPUESTA: { label: 'Enviada — pdte. respuesta', color: '#0ea5e9' },
  DESCARTADO: { label: 'Descartado', color: '#dc2626' },
  ACEPTADA: { label: 'Aceptada', color: '#10b981' },
} as const;

export type CotizEstado = keyof typeof COTIZ_ESTADOS;
export const COTIZ_ESTADOS_LIST: CotizEstado[] = Object.keys(COTIZ_ESTADOS) as CotizEstado[];

type Modificacion = {
  id: string;
  fecha_hora: string;
  usuario_email: string | null;
  accion: 'insert' | 'update' | 'delete';
  cambios: Record<string, unknown>;
  observaciones: string | null;
};

type CotizFull = {
  id: string;
  fecha: string;
  estado: CotizEstado;
  producto_id: string | null;
  cliente_id: string | null;
  subcategoria_id: string | null;
  proveedor_id: string | null;
  ref_proveedor: string | null;
  nombre_propuesto: string | null;
  unidades: number | null;
  coste_usd: number | string | null;
  cambio_usd_eur: number | string | null;
  precio_contenedor_eur: number | string | null;
  unidades_contenedor: number | null;
  envio_unidad_eur: number | string | null;
  foc_extra_pct: number | string | null;
  foc_proveedor_pct: number | string | null;
  aranceles_pct: number | string | null;
  coste_transporte_unidad_eur: number | string | null;
  margen_pct: number | string | null;
  comision_pm_pct: number | string | null;
  rappel_pct: number | string | null;
  marketing_pct: number | string | null;
  financiero_pct: number | string | null;
  coste_video_eur: number | string | null;
  coste_traduccion_eur: number | string | null;
  provisiones_eur: number | string | null;
  bolsa_eur: number | string | null;
  cdpe_eur: number | string | null;
  coste_basico_eur: number | string | null;
  coste_new_product_eur: number | string | null;
  coste_real_eur: number | string | null;
  coste_game_final_eur: number | string | null;
  pvpr_iva_eur: number | string | null;
  iva_pct: number | string | null;
  notas: string | null;
  main_specs: string | null;
  revision_pendiente: boolean;
  parametros_snapshot: Record<string, unknown> | null;
  cliente_codigo: string | null;
  cliente_nombre: string | null;
  marca_codigo: string | null;
  marca_nombre: string | null;
  categoria_id: string | null;
  categoria_nombre: string | null;
  subcategoria_nombre: string | null;
  proveedor_nombre: string | null;
  proveedor_email: string | null;
  producto_sku: string | null;
  producto_nombre: string | null;
  modificaciones: Modificacion[];
};

const num = (v: number | string | null | undefined): number => {
  if (v == null || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Respuesta de /precio/cotizaciones/:id/transporte-sugerido. `fuente` indica
// qué nivel de la cascada (producto > detalle > subcategoría > categoría)
// aportó el valor; 'ninguno' = no hay datos de transporte en la taxonomía.
type TransporteSugResp = {
  transporte_unidad_eur: number;
  units_40hq: number;
  fuente: 'producto' | 'detalle' | 'subcategoria' | 'categoria' | 'ninguno';
  precio_contenedor_eur: number;
};
const FUENTE_TRANSPORTE_LABEL: Record<TransporteSugResp['fuente'], string> = {
  producto: 'uds/contenedor del producto',
  detalle: 'datos del detalle',
  subcategoria: 'datos de la subcategoría',
  categoria: 'datos de la categoría',
  ninguno: 'sin datos de transporte en la taxonomía',
};

// Campos numéricos editables (todos los parámetros). Se persisten en
// PATCH /precio/cotizaciones/:id (whitelist en el backend).
type NumericKey =
  | 'unidades' | 'coste_usd' | 'cambio_usd_eur'
  | 'foc_proveedor_pct' | 'foc_extra_pct' | 'aranceles_pct'
  | 'coste_transporte_unidad_eur'
  | 'margen_pct' | 'comision_pm_pct' | 'rappel_pct' | 'marketing_pct' | 'financiero_pct'
  | 'coste_video_eur' | 'coste_traduccion_eur' | 'provisiones_eur' | 'bolsa_eur'
  | 'coste_game_final_eur' | 'pvpr_iva_eur' | 'iva_pct';

type Draft = Record<NumericKey, number> & {
  ref_proveedor: string;
  nombre_propuesto: string;
  notas: string;
  main_specs: string;
  proveedor_id: string;
  subcategoria_id: string;
  revision_pendiente: boolean;
};

const EMPTY_DRAFT: Draft = {
  unidades: 0, coste_usd: 0, cambio_usd_eur: 0,
  foc_proveedor_pct: 0, foc_extra_pct: 0, aranceles_pct: 0,
  coste_transporte_unidad_eur: 0,
  margen_pct: 0, comision_pm_pct: 0, rappel_pct: 0, marketing_pct: 0, financiero_pct: 0,
  coste_video_eur: 0, coste_traduccion_eur: 0, provisiones_eur: 0, bolsa_eur: 0,
  coste_game_final_eur: 0, pvpr_iva_eur: 0, iva_pct: 21,
  ref_proveedor: '', nombre_propuesto: '', notas: '', main_specs: '',
  proveedor_id: '', subcategoria_id: '', revision_pendiente: false,
};

function buildDraftFromCotiz(c: CotizFull): Draft {
  return {
    unidades: num(c.unidades),
    coste_usd: num(c.coste_usd),
    cambio_usd_eur: num(c.cambio_usd_eur),
    foc_proveedor_pct: num(c.foc_proveedor_pct),
    foc_extra_pct: num(c.foc_extra_pct),
    aranceles_pct: num(c.aranceles_pct),
    coste_transporte_unidad_eur: num(c.coste_transporte_unidad_eur),
    margen_pct: num(c.margen_pct),
    comision_pm_pct: num(c.comision_pm_pct),
    rappel_pct: num(c.rappel_pct),
    marketing_pct: num(c.marketing_pct),
    financiero_pct: num(c.financiero_pct),
    coste_video_eur: num(c.coste_video_eur),
    coste_traduccion_eur: num(c.coste_traduccion_eur),
    provisiones_eur: num(c.provisiones_eur),
    bolsa_eur: num(c.bolsa_eur),
    coste_game_final_eur: num(c.coste_game_final_eur),
    pvpr_iva_eur: num(c.pvpr_iva_eur),
    iva_pct: num(c.iva_pct) || 21,
    ref_proveedor: c.ref_proveedor ?? '',
    nombre_propuesto: c.nombre_propuesto ?? '',
    notas: c.notas ?? '',
    main_specs: c.main_specs ?? '',
    proveedor_id: c.proveedor_id ?? '',
    subcategoria_id: c.subcategoria_id ?? '',
    revision_pendiente: Boolean(c.revision_pendiente),
  };
}

export function CotizacionDetalle() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const detalle = useQuery({
    queryKey: ['cotizacion-full', id],
    queryFn: () => api.get<CotizFull>(`/precio/cotizaciones/${id}/full`),
    enabled: !!id,
  });

  // Base del cliente para volver/navegar. Si la cotización es de TICNOVA,
  // todo apunta a /ticnova/...; si es GAME, a /game/... Mientras se carga
  // el detalle, fallback a /game.
  const clienteBase = useMemo(() => {
    const code = detalle.data?.cliente_codigo;
    return code === 'TICNOVA' ? '/ticnova' : '/game';
  }, [detalle.data?.cliente_codigo]);
  const volver = useVolver(() => `${clienteBase}/cotizaciones`);

  const proveedores = useQuery({
    queryKey: ['list', '/proveedores'],
    queryFn: () => api.get<{ data: { id: string; nombre: string }[] }>('/proveedores'),
  });
  const subcategorias = useQuery({
    queryKey: ['list', '/admin/subcategorias'],
    queryFn: () => api.get<{ data: { id: string; nombre: string }[] }>('/admin/subcategorias'),
  });
  const detalles = useQuery({
    queryKey: ['list', '/admin/detalles'],
    queryFn: () => api.get<{ data: { id: string; nombre: string; subcategoria_id: string; activo: boolean }[] }>('/admin/detalles'),
  });

  // Producto proveedor asociado (para imagen + link)
  const c0 = detalle.data;
  const ppLookup = useQuery({
    queryKey: ['pp-lookup', c0?.proveedor_id, c0?.ref_proveedor],
    queryFn: async () => {
      const res = await api.get<{ data: { id: string; ref_proveedor: string }[] }>(
        `/productos-proveedor?proveedor_id=${c0!.proveedor_id}&ref_proveedor=${encodeURIComponent(c0!.ref_proveedor!)}&limit=1`,
      );
      return res.data?.[0] ?? null;
    },
    enabled: !!c0?.proveedor_id && !!c0?.ref_proveedor,
  });
  const ppId = ppLookup.data?.id ?? null;
  type Archivo = { id: string; tipo: string; url: string | null };
  const ppArchivos = useQuery({
    queryKey: ['pp-archivos-img', ppId],
    queryFn: () => api.get<{ data: Archivo[] }>(`/productos-proveedor/${ppId}/archivos`),
    enabled: !!ppId,
  });
  const imagenUrl = useMemo(() => {
    if (!ppArchivos.data) return null;
    const img = (ppArchivos.data.data ?? []).find((a) => a.tipo === 'imagen_principal');
    return img?.url ?? null;
  }, [ppArchivos.data]);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [observaciones, setObservaciones] = useState('');
  // Garantizar margen mínimo Approx: copiamos el valor del snapshot
  // (si la cotización fue creada con ese flag) o lo dejamos en false.
  // El CEO puede des/marcarlo desde la ficha.
  const [garantizarMargen, setGarantizarMargen] = useState<boolean>(false);
  // Si el usuario toca el "Coste final" en modo edición, ya no lo
  // auto-sobrescribimos con el resultado del recalculador.
  const [costeFinalManual, setCosteFinalManual] = useState(false);
  // Detalle (4ª capa de taxonomía). Vive en parametros_snapshot.detalle_id.
  const [detalleId, setDetalleId] = useState('');
  // Metadatos del último "Recalcular transporte" (fuente + contenedor usado),
  // para mostrar un hint bajo el campo. Se limpia si el CEO lo pisa a mano.
  const [transporteSugMeta, setTransporteSugMeta] = useState<TransporteSugResp | null>(null);

  // Pre-cargar draft cuando llegan los datos.
  useEffect(() => {
    if (!detalle.data) return;
    setDraft(buildDraftFromCotiz(detalle.data));
    const snap = (detalle.data.parametros_snapshot ?? {}) as Record<string, unknown>;
    // Marcado por defecto: estándar del holding desde mig 055 — todas las
    // condiciones garantizan margen mínimo. Solo sale desmarcado si el
    // snapshot lo guardó EXPLÍCITAMENTE como false (opt-out manual del CEO).
    setGarantizarMargen(snap.garantiza_margen_minimo !== false);
    setDetalleId(typeof snap.detalle_id === 'string' ? snap.detalle_id : '');
    setTransporteSugMeta(null);
  }, [detalle.data]);

  // ── Recálculo en vivo: cuando el draft cambia en modo edición, llamamos
  // al endpoint /precio/calcular-completo (misma calculadora). Debounce
  // 250ms para no saturar. Los subtotales mostrados pasan a usar el
  // resultado calculado en lugar del snapshot.
  type CalcRes = {
    cdpe_eur: number;
    coste_basico_eur: number;
    coste_new_product_eur: number;
    coste_real_eur: number;
  };
  const calc = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<CalcRes>('/precio/calcular-completo', body),
  });
  useEffect(() => {
    if (!editing) return;
    const body = {
      unidades: draft.unidades,
      coste_usd: draft.coste_usd,
      foc_extra_pct: draft.foc_extra_pct,
      aranceles_pct: draft.aranceles_pct,
      cambio_usd_eur: draft.cambio_usd_eur,
      coste_contenedor_eur: 0,
      unidades_40hq: 0,
      coste_transporte_unidad_eur: draft.coste_transporte_unidad_eur,
      margen_approx_pct: draft.margen_pct,
      comision_pm_pct: draft.comision_pm_pct,
      rappel_game_pct: draft.rappel_pct,
      mkt_game_pct: draft.marketing_pct,
      financiero_pct: draft.financiero_pct,
      // El draft refleja lo que el CEO ve en la UI (0 por defecto si el
      // cliente no aplica, o el valor editado manualmente). Lo enviamos
      // tal cual al recalculador para que el "COSTE NEW PRODUCT" cuadre
      // con lo que se muestra.
      coste_video_eur: draft.coste_video_eur,
      coste_traduccion_eur: draft.coste_traduccion_eur,
      provisiones_eur: draft.provisiones_eur,
      bolsa_eur: draft.bolsa_eur,
    };
    const t = setTimeout(() => calc.mutate(body), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, draft.unidades, draft.coste_usd, draft.foc_extra_pct, draft.aranceles_pct,
      draft.cambio_usd_eur, draft.coste_transporte_unidad_eur, draft.margen_pct,
      draft.comision_pm_pct, draft.rappel_pct, draft.marketing_pct, draft.financiero_pct,
      draft.coste_video_eur, draft.coste_traduccion_eur, draft.provisiones_eur, draft.bolsa_eur]);

  // Precio mínimo para garantizar el margen objetivo. Si la garantía
  // está activa y el coste base quedaría por debajo, lo subimos.
  const precioMinGarantizado = useMemo<number | null>(() => {
    if (!garantizarMargen || !calc.data) return null;
    const margenObj = draft.margen_pct;
    if (margenObj <= 0 || margenObj >= 100) return null;
    const desc = (draft.rappel_pct + draft.comision_pm_pct + draft.marketing_pct) / 100;
    if (desc >= 1) return null;
    const extras = draft.unidades > 0
      ? (draft.coste_video_eur + draft.coste_traduccion_eur) / draft.unidades
      : 0;
    const ventaNetaMin = calc.data.cdpe_eur / (1 - margenObj / 100);
    return (ventaNetaMin + extras) / (1 - desc);
  }, [garantizarMargen, calc.data, draft.margen_pct, draft.rappel_pct,
      draft.comision_pm_pct, draft.marketing_pct, draft.coste_video_eur,
      draft.coste_traduccion_eur, draft.unidades]);

  // La garantía de margen mínimo es un SUELO, no un valor fijo. Al entrar en
  // edición (o al recalcular parámetros) solo subimos el Coste final cuando el
  // valor guardado dejaría a Approx por debajo del margen objetivo. Si ya da
  // margen suficiente NO lo tocamos: preservamos el precio que fijó el CEO (y
  // su margen, que puede ser mayor que el mínimo). Nunca lo bajamos. Si el CEO
  // lo editó a mano, tampoco lo pisamos. Mismo criterio que la calculadora.
  useEffect(() => {
    if (!editing || costeFinalManual || !garantizarMargen || precioMinGarantizado == null) return;
    const piso = Math.ceil(precioMinGarantizado * 100) / 100; // CEIL al céntimo → margen ≥ objetivo
    setDraft((d) => (d.coste_game_final_eur >= piso ? d : { ...d, coste_game_final_eur: piso }));
  }, [editing, costeFinalManual, garantizarMargen, precioMinGarantizado]);

  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch<CotizFull>(`/precio/cotizaciones/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cotizacion-full', id] });
      qc.invalidateQueries({ queryKey: ['cotizaciones'] });
      setEditing(false);
      setObservaciones('');
    },
  });

  // Recalcular SOLO el transporte unitario con la cascada de la app
  // (producto > detalle > subcategoría > categoría) + contenedor vigente.
  // Rellena el campo editable; el CEO puede pisarlo a mano después.
  const recalcTransporte = useMutation({
    mutationFn: () => api.get<TransporteSugResp>(`/precio/cotizaciones/${id}/transporte-sugerido`),
    onSuccess: (r) => {
      setDraft((d) => ({ ...d, coste_transporte_unidad_eur: r.transporte_unidad_eur }));
      setTransporteSugMeta(r);
    },
  });

  const del = useMutation({
    mutationFn: () => api.delete<{ ok: true }>(`/precio/cotizaciones/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cotizaciones'] });
      navigate(`${clienteBase}/cotizaciones`);
    },
  });

  const cambiarEstado = (e: CotizEstado) => {
    update.mutate({ estado: e, observaciones: observaciones.trim() || null });
  };

  const guardar = () => {
    const body: Record<string, unknown> = {};
    for (const k of Object.keys(draft) as (keyof Draft)[]) {
      const v = draft[k];
      if (v === '') body[k] = null;
      else body[k] = v;
    }
    body.garantiza_margen_minimo = garantizarMargen;
    body.detalle_id = detalleId || null; // vive en parametros_snapshot
    if (observaciones.trim()) body.observaciones = observaciones.trim();
    update.mutate(body);
  };

  if (detalle.isLoading || !detalle.data) return <Spinner />;
  if (detalle.isError) return <p className="text-rose-700">No se pudo cargar la cotización.</p>;
  const c = detalle.data;
  const estadoMeta = COTIZ_ESTADOS[c.estado] ?? { label: c.estado, color: '#64748b' };
  const titulo = c.producto_sku ?? c.nombre_propuesto ?? c.ref_proveedor ?? 'Cotización';
  // TICNOVA y cualquier otro cliente con aplica_costes_new_product=false
  // no usa los costes de vídeo+traducción. Como la cotización ya está
  // guardada y no podemos releer el flag desde aquí sin un useQuery
  // adicional, usamos el cliente_codigo como heurística (default true).
  const aplicaNewProduct = c.cliente_codigo !== 'TICNOVA';

  return (
    <div className="space-y-4">
      <PageHeader
        title={titulo}
        subtitle={`Cotización del ${fmt.datetime(c.fecha)} · ${c.cliente_nombre ?? '—'} · ${c.proveedor_nombre ?? 'sin proveedor'}`}
        actions={
          <>
            <Button variant="secondary" onClick={volver}>← Volver</Button>
            <Button
              variant="secondary"
              onClick={() => {
                openCotizMailto([{
                  producto_nombre: c.producto_nombre,
                  nombre_propuesto: c.nombre_propuesto,
                  ref_proveedor: c.ref_proveedor,
                  subcategoria_nombre: c.subcategoria_nombre,
                  unidades: c.unidades,
                  coste_game_final_eur: c.coste_game_final_eur,
                  main_specs: c.main_specs,
                }]);
              }}
              title="Descarga un .eml con la tabla en HTML y lo abre en Outlook como borrador editable. El destinatario lo pones tú en Outlook."
            >
              📧 Email
            </Button>
            {editing ? (
              <>
                <Button variant="danger" onClick={() => { setEditing(false); setDraft(buildDraftFromCotiz(c)); setObservaciones(''); setCosteFinalManual(false); setTransporteSugMeta(null); }}>Cancelar</Button>
                <Button variant="save" onClick={guardar} disabled={update.isPending}>
                  {update.isPending ? 'Guardando…' : 'Guardar'}
                </Button>
              </>
            ) : (
              <Button variant="edit" onClick={() => { setEditing(true); setCosteFinalManual(false); }}>✏️ Editar</Button>
            )}
            <Button
              variant="danger"
              onClick={async () => { if (await confirmar({ title: '¿Eliminar esta cotización?', message: 'Se mantiene en el audit log.', confirmLabel: 'Eliminar', danger: true })) del.mutate(); }}
              disabled={del.isPending}
            >
              🗑 {del.isPending ? 'Eliminando…' : 'Eliminar'}
            </Button>
          </>
        }
      />

      {c.revision_pendiente && !editing && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <span className="text-base">⚠️</span>
          <div className="flex-1">
            <p className="font-semibold">Cotización pendiente de revisar.</p>
            <p className="text-xs text-amber-800">
              Creada automáticamente (cotización masiva). Revisa los parámetros, ajusta coste y PVPr, y desmarca el flag editando la cotización.
            </p>
          </div>
        </div>
      )}

      {/* Identidad — estado como parte del título de la caja (esquina dcha) */}
      <Card
        title="Identidad"
        description="La fecha de creación no se modifica. Cambia el estado o edita el resto."
        actions={
          <div className="flex items-center gap-2">
            <Badge color={estadoMeta.color}>{estadoMeta.label}</Badge>
            <Select
              options={COTIZ_ESTADOS_LIST.map((e) => ({ value: e, label: COTIZ_ESTADOS[e].label }))}
              value={c.estado}
              emptyLabel={null}
              onChange={(e) => cambiarEstado(e.target.value as CotizEstado)}
              className="w-52"
            />
          </div>
        }
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          {imagenUrl && (
            <img
              src={imagenUrl}
              alt="Producto"
              className="h-32 w-32 shrink-0 rounded-lg border border-slate-200 bg-white object-contain"
            />
          )}
          <div className="grid min-w-0 flex-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Cliente">{c.cliente_nombre ?? '—'}</Field>
            <Field label="Marca">{c.marca_nombre ?? '—'}</Field>
            <Field label="Categoría">{c.categoria_nombre ?? '—'}</Field>
            <Field label="Subcategoría">
              {editing ? (
                <Select
                  options={(subcategorias.data?.data ?? []).map((s) => ({ value: s.id, label: s.nombre }))}
                  value={draft.subcategoria_id}
                  emptyLabel="— sin asignar —"
                  onChange={(e) => { setDraft((d) => ({ ...d, subcategoria_id: e.target.value })); setDetalleId(''); }}
                />
              ) : (
                c.subcategoria_nombre ?? '—'
              )}
            </Field>
            <Field label="Detalle">
              {editing ? (
                <Select
                  options={(detalles.data?.data ?? [])
                    .filter((d) => d.activo && (!draft.subcategoria_id || d.subcategoria_id === draft.subcategoria_id))
                    .map((d) => ({ value: d.id, label: d.nombre }))}
                  value={detalleId}
                  emptyLabel="— sin detalle —"
                  onChange={(e) => setDetalleId(e.target.value)}
                />
              ) : (
                (detalles.data?.data ?? []).find((d) => d.id === detalleId)?.nombre ?? '—'
              )}
            </Field>
            <Field label="Proveedor">
              {editing ? (
                <Select
                  options={(proveedores.data?.data ?? []).map((p) => ({ value: p.id, label: p.nombre }))}
                  value={draft.proveedor_id}
                  emptyLabel="— sin asignar —"
                  onChange={(e) => setDraft((d) => ({ ...d, proveedor_id: e.target.value }))}
                />
              ) : (
                c.proveedor_nombre ?? '—'
              )}
            </Field>
            <Field label="Ref. proveedor">
              {editing ? (
                <Input value={draft.ref_proveedor} onChange={(e) => setDraft((d) => ({ ...d, ref_proveedor: e.target.value }))} />
              ) : (
                <span className="inline-flex items-center gap-2">
                  <span className="font-mono text-sm">{c.ref_proveedor ?? '—'}</span>
                  {ppId && (
                    <a
                      className="cursor-pointer text-sm font-medium text-sky-600 hover:underline"
                      href={`/proveedores/productos/${ppId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Abrir la ficha del producto de proveedor en una pestaña nueva"
                    >
                      Ver ficha
                    </a>
                  )}
                </span>
              )}
            </Field>
            <Field label="Nombre propuesto">
              {editing ? (
                <Input value={draft.nombre_propuesto} onChange={(e) => setDraft((d) => ({ ...d, nombre_propuesto: e.target.value }))} />
              ) : (
                c.nombre_propuesto ?? '—'
              )}
            </Field>
            <Field label="Producto enlazado">
              {c.producto_sku ? (
                <a
                  className="cursor-pointer text-sky-600 hover:underline"
                  href={`${clienteBase}/productos/${c.producto_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={c.producto_nombre ?? undefined}
                >
                  <span className="font-mono">{c.producto_sku}</span>
                </a>
              ) : '—'}
            </Field>
            <Field label="Tipo de envío">
              {(c.parametros_snapshot?.envio_tipo as string) || '—'}
            </Field>
            <Field label="Packaging incluido">
              {c.parametros_snapshot?.packaging_included == null
                ? '—'
                : (c.parametros_snapshot.packaging_included ? 'Sí' : 'No')}
            </Field>
          </div>
        </div>
        <div className="mt-4">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Main specs / resumen técnico</div>
          {editing ? (
            <Textarea
              rows={3}
              value={draft.main_specs}
              onChange={(e) => setDraft((d) => ({ ...d, main_specs: e.target.value }))}
              placeholder="Ej: HDMI 2.0, 144Hz, 1ms response time, ajustable en altura…"
            />
          ) : (
            <p className="whitespace-pre-line text-sm text-slate-700">
              {c.main_specs || <span className="italic text-slate-400">— sin especificar —</span>}
            </p>
          )}
        </div>
      </Card>

      {/* Parámetros aplicados — tipo calculadora */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card
          title="Parámetros aplicados"
          description={editing
            ? 'Editando — los subtotales se recalculan en vivo (mismo motor que la calculadora). Pulsa Guardar para persistir.'
            : 'Snapshot guardado. Pulsa "Editar" para ajustar parámetros con recálculo en vivo.'}
          className="lg:col-span-2"
        >
          <div className="grid gap-x-5 gap-y-1 lg:grid-cols-3">
            <RowNum label="Unidades / MOQ" value={draft.unidades} onChange={(v) => setDraft((d) => ({ ...d, unidades: v }))} suffix="ud" disabled={!editing} />
            <RowNum label="Coste fábrica" value={draft.coste_usd} onChange={(v) => setDraft((d) => ({ ...d, coste_usd: v }))} suffix="USD" disabled={!editing} />
            <RowNum label="F.O.C. Incluido Proveedor" value={draft.foc_proveedor_pct} onChange={(v) => setDraft((d) => ({ ...d, foc_proveedor_pct: v }))} suffix="%" disabled={!editing} hint="Informativo — no recalcula." />
            <RowNum label="F.O.C. extra" value={draft.foc_extra_pct} onChange={(v) => setDraft((d) => ({ ...d, foc_extra_pct: v }))} suffix="%" disabled={!editing} />
            <RowNum label="Aranceles" value={draft.aranceles_pct} onChange={(v) => setDraft((d) => ({ ...d, aranceles_pct: v }))} suffix="%" disabled={!editing} />
            <RowNum label="Cambio USD→EUR" value={draft.cambio_usd_eur} onChange={(v) => setDraft((d) => ({ ...d, cambio_usd_eur: v }))} disabled={!editing} />
            <div className="flex flex-wrap items-center gap-2 py-1 lg:col-span-3">
              <div className="min-w-0 flex-1">
                <span className="block truncate text-sm text-slate-700" title="Coste transporte ud">Coste transporte ud</span>
                {transporteSugMeta && (
                  <p className="truncate text-[10px] leading-tight text-emerald-700" title={`Recalculado según ${FUENTE_TRANSPORTE_LABEL[transporteSugMeta.fuente]} · contenedor ${fmt.eur(transporteSugMeta.precio_contenedor_eur)}`}>
                    {transporteSugMeta.fuente === 'ninguno'
                      ? 'Sin datos de transporte en la taxonomía'
                      : `Recalculado según ${FUENTE_TRANSPORTE_LABEL[transporteSugMeta.fuente]} (contenedor ${fmt.eur(transporteSugMeta.precio_contenedor_eur)})`}
                  </p>
                )}
              </div>
              <DecimalInput
                value={draft.coste_transporte_unidad_eur}
                onChange={(v) => { setDraft((d) => ({ ...d, coste_transporte_unidad_eur: v })); setTransporteSugMeta(null); }}
                decimals={2}
                readOnly={!editing}
                ariaLabel="Coste transporte ud"
                style={{ width: '6rem' }}
                className="shrink-0 text-right font-mono text-sm"
              />
              <span style={{ width: '2.25rem' }} className="shrink-0 text-xs text-slate-500">EUR</span>
              {editing && (
                <Button
                  size="sm"
                  variant="create"
                  type="button"
                  onClick={() => recalcTransporte.mutate()}
                  disabled={recalcTransporte.isPending}
                  className="shrink-0"
                  title="Recalcula el transporte con la cascada de la app: coste/uds del producto → datos del detalle → subcategoría → categoría, aplicando el precio de contenedor actual. Puedes pisarlo a mano después."
                >
                  {recalcTransporte.isPending ? '…' : '↻ Recalcular'}
                </Button>
              )}
            </div>
          </div>

          <Subtotal label="CDPe — Coste después de transporte" value={editing && calc.data ? calc.data.cdpe_eur : num(c.cdpe_eur)} />

          <div className="mt-2 grid gap-x-5 gap-y-1 lg:grid-cols-3">
            <RowNum label="Margen Approx" value={draft.margen_pct} onChange={(v) => setDraft((d) => ({ ...d, margen_pct: v }))} suffix="%" disabled={!editing} />
            <RowNum label="Comisión PM" value={draft.comision_pm_pct} onChange={(v) => setDraft((d) => ({ ...d, comision_pm_pct: v }))} suffix="%" disabled={!editing} />
            <RowNum label="Rappel cliente" value={draft.rappel_pct} onChange={(v) => setDraft((d) => ({ ...d, rappel_pct: v }))} suffix="%" disabled={!editing} />
            <RowNum label="Marketing" value={draft.marketing_pct} onChange={(v) => setDraft((d) => ({ ...d, marketing_pct: v }))} suffix="%" disabled={!editing} />
            <RowNum label="Coste financiero" value={draft.financiero_pct} onChange={(v) => setDraft((d) => ({ ...d, financiero_pct: v }))} suffix="%" disabled={!editing} />
          </div>

          <label className="mt-3 flex cursor-pointer items-start gap-2 rounded-md border border-slate-200 bg-white p-2 text-xs text-slate-700">
            <input
              type="checkbox"
              checked={garantizarMargen}
              onChange={(e) => setGarantizarMargen(e.target.checked)}
              disabled={!editing}
              className="mt-0.5 h-4 w-4 cursor-pointer"
            />
            <div className="flex-1">
              <span className="block font-semibold text-slate-800">
                Garantizar margen mínimo Approx ({fmt.pct(draft.margen_pct)})
              </span>
              <span className="block text-slate-500">
                Si el coste final dejaría a Approx con menos margen del estipulado, sube el precio facturado hasta el mínimo necesario. Por defecto se copia de la condición comercial activa al crear la cotización.
              </span>
              {editing && !costeFinalManual && precioMinGarantizado != null && (() => {
                const piso = Math.ceil(precioMinGarantizado * 100) / 100;
                // Solo avisamos cuando el coste final está pegado al mínimo (la
                // garantía es la restricción activa). Si está por encima, hay
                // margen de sobra y no se ha tocado nada.
                if (draft.coste_game_final_eur > piso) return null;
                return (
                  <span className="mt-1 block text-emerald-700">
                    Coste final en el mínimo para garantizar el {fmt.pct(draft.margen_pct)} ({fmt.eur(piso)}).
                  </span>
                );
              })()}
            </div>
          </label>

          <Subtotal label="COSTE BÁSICO" value={editing && calc.data ? calc.data.coste_basico_eur : num(c.coste_basico_eur)} />

          <div className="mt-2 grid gap-x-5 gap-y-1 lg:grid-cols-3">
            <RowNum
              label="Coste video"
              value={draft.coste_video_eur}
              onChange={(v) => setDraft((d) => ({ ...d, coste_video_eur: v }))}
              suffix="EUR"
              disabled={!editing}
              hint={!aplicaNewProduct ? 'Cliente sin coste vídeo por defecto — editable.' : undefined}
            />
            <RowNum
              label="Coste traducción"
              value={draft.coste_traduccion_eur}
              onChange={(v) => setDraft((d) => ({ ...d, coste_traduccion_eur: v }))}
              suffix="EUR"
              disabled={!editing}
              hint={!aplicaNewProduct ? 'Cliente sin coste traducción por defecto — editable.' : undefined}
            />
          </div>
          <Subtotal label="COSTE NEW PRODUCT" value={editing && calc.data ? calc.data.coste_new_product_eur : num(c.coste_new_product_eur)} />

          <div className="mt-2 grid gap-x-5 gap-y-1 lg:grid-cols-3">
            <RowNum label="Provisiones" value={draft.provisiones_eur} onChange={(v) => setDraft((d) => ({ ...d, provisiones_eur: v }))} suffix="EUR" hint="Se suma" disabled={!editing} />
            <RowNum label="Bolsa" value={draft.bolsa_eur} onChange={(v) => setDraft((d) => ({ ...d, bolsa_eur: v }))} suffix="EUR" hint="Se resta" disabled={!editing} />
          </div>

          <Subtotal label="COSTE REAL" value={editing && calc.data ? calc.data.coste_real_eur : num(c.coste_real_eur)} bold />
        </Card>

        <Card title="Resultado">
          <div className="space-y-3">
            <PanelLine label="CDPe" value={editing && calc.data ? calc.data.cdpe_eur : num(c.cdpe_eur)} />
            <PanelLine label="Coste básico" value={editing && calc.data ? calc.data.coste_basico_eur : num(c.coste_basico_eur)} />
            <PanelLine label="Coste new product" value={editing && calc.data ? calc.data.coste_new_product_eur : num(c.coste_new_product_eur)} />
            <PanelLine label="Coste real" value={editing && calc.data ? calc.data.coste_real_eur : num(c.coste_real_eur)} highlight />
          </div>

          <div className="mt-4 rounded-md bg-amber-50 px-3 py-2 ring-1 ring-amber-200">
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600">
              Coste final
            </label>
            <div className="mt-1 flex items-center gap-2">
              <DecimalInput
                value={draft.coste_game_final_eur}
                onChange={(v) => { setCosteFinalManual(true); setDraft((d) => ({ ...d, coste_game_final_eur: v })); }}
                decimals={2}
                ariaLabel="Coste final"
                className="flex-1 text-right font-mono"
                readOnly={!editing}
              />
              <span className="w-8 text-xs text-slate-500">EUR</span>
            </div>
          </div>

          {(() => {
            // Margen real Approx: del precio facturado al cliente (coste
            // final) descontamos:
            //  - Rappel cliente + Comisión PM + Marketing (% sobre el
            //    precio facturado, salen del bolsillo de Approx).
            //  - El incremento por vídeo + traducción ya imputado al
            //    precio facturado: Approx paga ese importe al externo,
            //    no es rentabilidad. Para TICNOVA estos suelen ser 0.
            // El financiero NO se descuenta (coste interno de Approx).
            // Coste real para Approx = CDPe + provisiones/u − bolsa/u: el dinero
            // aprovisionado (a recuperar) es coste real para Approx y la bolsa
            // (saldo a favor del cliente) lo reduce; ambos por unidad.
            const precioFacturado = draft.coste_game_final_eur;
            const descuentoPct = (draft.rappel_pct + draft.comision_pm_pct + draft.marketing_pct) / 100;
            const descuentoPctEur = precioFacturado * descuentoPct;
            const extrasUnidad = draft.unidades > 0
              ? (draft.coste_video_eur + draft.coste_traduccion_eur) / draft.unidades
              : 0;
            const ventaNeta = precioFacturado - descuentoPctEur - extrasUnidad;
            const cdpe = editing && calc.data ? calc.data.cdpe_eur : num(c.cdpe_eur);
            const provUnidad = draft.unidades > 0 ? draft.provisiones_eur / draft.unidades : 0;
            const bolsaUnidad = draft.unidades > 0 ? draft.bolsa_eur / draft.unidades : 0;
            const costeRealApprox = cdpe + provUnidad - bolsaUnidad;
            const margenAbs = ventaNeta - costeRealApprox;
            // Margen comercial estándar: sobre la VENTA NETA (lo que
            // Approx efectivamente cobra al cliente, descontando lo que
            // se le devuelve por rappel/comisión PM/marketing y los
            // costes externos imputados por unidad).
            const margenPct = ventaNeta > 0 ? (margenAbs / ventaNeta) * 100 : null;
            const positivo = margenAbs >= 0;
            return (
              <div className={`mt-3 rounded-md px-3 py-2 ring-1 ${positivo ? 'bg-emerald-50 ring-emerald-300' : 'bg-rose-50 ring-rose-300'}`}>
                <label
                  className={`block text-xs font-semibold uppercase tracking-wide ${positivo ? 'text-emerald-800' : 'text-rose-800'}`}
                  title="Margen sobre venta neta: margen € / venta neta × 100. Es decir, el margen sobre lo que Approx realmente cobra al cliente tras descontar rappel/comisión PM/marketing y los costes externos (vídeo/traducción)."
                >
                  Margen real Approx (sobre venta neta)
                </label>
                <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
                  <span className="text-slate-600">Precio facturado</span>
                  <span className="text-right font-mono">{fmt.eur(precioFacturado)}</span>
                  <span className="text-slate-600" title="Rappel cliente + Comisión PM + Marketing aplicados al precio facturado. El financiero NO se descuenta (coste interno).">
                    − descuentos % ({fmt.pct(descuentoPct * 100)})
                  </span>
                  <span className="text-right font-mono text-slate-600">−{fmt.eur(descuentoPctEur)}</span>
                  {extrasUnidad > 0 && (
                    <>
                      <span className="text-slate-600" title="Coste de vídeo y traducción por unidad. Imputados al precio facturado pero pagados a un externo (no es rentabilidad de Approx).">
                        − vídeo+traducción/ud
                      </span>
                      <span className="text-right font-mono text-slate-600">−{fmt.eur(extrasUnidad)}</span>
                    </>
                  )}
                  <span className="text-slate-600">Venta neta</span>
                  <span className="text-right font-mono">{fmt.eur(ventaNeta)}</span>
                  <span className="text-slate-600">− CDPe</span>
                  <span className="text-right font-mono text-slate-600">−{fmt.eur(cdpe)}</span>
                  {provUnidad > 0 && (
                    <>
                      <span className="text-slate-600" title="Dinero aprovisionado por unidad (a recuperar). Es coste real para Approx, reduce el margen.">
                        − provisiones/ud
                      </span>
                      <span className="text-right font-mono text-slate-600">−{fmt.eur(provUnidad)}</span>
                    </>
                  )}
                  {bolsaUnidad > 0 && (
                    <>
                      <span className="text-slate-600" title="Bolsa (saldo a favor del cliente) por unidad. Reduce el coste real de Approx, aumenta el margen.">
                        + bolsa/ud
                      </span>
                      <span className="text-right font-mono text-slate-600">+{fmt.eur(bolsaUnidad)}</span>
                    </>
                  )}
                  <span className={`font-semibold ${positivo ? 'text-emerald-900' : 'text-rose-900'}`}>= Margen Approx</span>
                  <span className={`text-right font-mono font-bold ${positivo ? 'text-emerald-900' : 'text-rose-900'}`}>
                    {fmt.eur(margenAbs)} ({margenPct != null ? fmt.pct(margenPct) : '—'})
                  </span>
                </div>
              </div>
            );
          })()}

          {(() => {
            // Margen del cliente final (GAME/TICNOVA): lo que gana revendiendo.
            // Compara el PVPr SIN IVA (su ingreso real) contra el Coste final
            // (lo que paga a Approx, también sin IVA). Mismo cálculo que la
            // calculadora ("Margen cliente") para que coincidan los números.
            const pvpr = draft.pvpr_iva_eur;
            const pvprSinIva = pvpr > 0 && draft.iva_pct > -100 ? pvpr / (1 + draft.iva_pct / 100) : null;
            const costeFinal = draft.coste_game_final_eur;
            const margenCliente = pvprSinIva != null && pvprSinIva > 0 && costeFinal > 0
              ? ((pvprSinIva - costeFinal) / pvprSinIva) * 100
              : null;
            return (
              <div className="mt-3 rounded-md bg-slate-900 px-3 py-3 text-white">
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-300">
                  PVPr con IVA
                </label>
                <div className="mt-1 flex items-center gap-2">
                  <DecimalInput
                    value={draft.pvpr_iva_eur}
                    onChange={(v) => setDraft((d) => ({ ...d, pvpr_iva_eur: v }))}
                    decimals={2}
                    ariaLabel="PVPr con IVA"
                    className="flex-1 text-right font-mono text-slate-900"
                    readOnly={!editing}
                  />
                  <span className="w-8 text-xs text-slate-400">EUR</span>
                </div>
                <div className="mt-2 flex items-center justify-between gap-3 text-xs">
                  <span className="text-slate-400">
                    IVA <RowNumInline value={draft.iva_pct} onChange={(v) => setDraft((d) => ({ ...d, iva_pct: v }))} disabled={!editing} suffix="%" />
                  </span>
                  <span
                    className={`inline-flex items-baseline gap-1.5 ${margenCliente != null ? 'text-emerald-300' : 'text-slate-500'}`}
                    title="Margen del cliente final: (PVPr sin IVA − Coste final) / PVPr sin IVA × 100. Lo que gana el cliente revendiendo respecto a lo que paga a Approx (ambos importes sin IVA)."
                  >
                    <span className="uppercase tracking-wide text-[0.65rem]">Margen cliente</span>
                    <span className="font-mono text-sm font-bold">{margenCliente != null ? fmt.pct(margenCliente) : '—'}</span>
                  </span>
                </div>
              </div>
            );
          })()}

          <div className="mt-4">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Notas internas</label>
            {editing ? (
              <Textarea rows={3} value={draft.notas} onChange={(e) => setDraft((d) => ({ ...d, notas: e.target.value }))} />
            ) : (
              <p className="whitespace-pre-line text-sm text-slate-700">{c.notas || <span className="italic text-slate-400">— sin notas —</span>}</p>
            )}
          </div>

          {editing && (
            <div className="mt-3">
              <label className="inline-flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={draft.revision_pendiente}
                  onChange={(e) => setDraft((d) => ({ ...d, revision_pendiente: e.target.checked }))}
                  className="h-4 w-4 cursor-pointer rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                />
                <span className="text-sm text-slate-700">
                  {draft.revision_pendiente ? '⚠️ Pdte. de revisar' : 'Revisada'}
                </span>
              </label>
            </div>
          )}

          {editing && (
            <div className="mt-3">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Observación de este cambio</label>
              <Input
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                placeholder="Texto libre que se guarda en el audit log junto al diff"
              />
            </div>
          )}
        </Card>
      </div>

      {/* Audit log */}
      <Card title="Registro de cambios" description="Cada insert/update/delete queda registrado con autor + diff.">
        <ModificacionesLog modificaciones={c.modificaciones} />
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Subcomponentes
// ─────────────────────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-sm text-slate-900">{children}</div>
    </div>
  );
}

function RowNum({ label, value, onChange, suffix, hint, disabled }: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  suffix?: string;
  hint?: string;
  disabled?: boolean;
}) {
  // Norma del holding: 2 decimales en TODA la app (incl. %, cambio FX, etc.).
  // Solo las unidades van sin decimales.
  const decimals = suffix === 'ud' ? 0 : 2;
  return (
    <div className="flex items-center gap-2 py-1">
      <div className="min-w-0 flex-1">
        <span className="block truncate text-sm text-slate-700" title={label}>{label}</span>
        {hint && <p className="truncate text-[10px] leading-tight text-slate-400" title={hint}>{hint}</p>}
      </div>
      <DecimalInput
        value={value}
        onChange={onChange}
        decimals={decimals}
        readOnly={disabled}
        ariaLabel={label}
        style={{ width: '6rem' }}
        className="shrink-0 text-right font-mono text-sm"
      />
      <span style={{ width: '2.25rem' }} className="shrink-0 text-xs text-slate-500">{suffix ?? ''}</span>
    </div>
  );
}

function RowNumInline({ value, onChange, suffix, disabled }: {
  value: number;
  onChange: (v: number) => void;
  suffix?: string;
  disabled?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-1">
      <DecimalInput
        value={value}
        onChange={onChange}
        decimals={2}
        readOnly={disabled}
        style={{ width: '4rem' }}
        className="inline-block rounded border border-slate-700 bg-slate-800 px-1 py-0.5 text-right font-mono text-xs text-white"
      />
      {suffix && <span className="text-xs">{suffix}</span>}
    </span>
  );
}

function Subtotal({ label, value, bold }: { label: string; value: number; bold?: boolean }) {
  return (
    <div className={`mt-2 flex items-center justify-between rounded-md bg-slate-100 px-3 py-1.5 text-sm ${bold ? 'font-bold' : 'font-medium'}`}>
      <span className="uppercase tracking-wide text-xs text-slate-600">{label}</span>
      <span className="font-mono">{fmt.eur(value)}</span>
    </div>
  );
}

function PanelLine({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div className={`flex items-center justify-between rounded-md px-3 py-1.5 text-sm ${highlight ? 'bg-slate-900 text-white' : 'bg-slate-50'}`}>
      <span className={`text-xs uppercase tracking-wide ${highlight ? 'text-slate-300' : 'text-slate-500'}`}>{label}</span>
      <span className="font-mono">{fmt.eur(value)}</span>
    </div>
  );
}

function ModificacionesLog({ modificaciones }: { modificaciones: Modificacion[] }) {
  if (modificaciones.length === 0) {
    return <p className="text-xs italic text-slate-400">Sin cambios registrados.</p>;
  }
  const colorBy = {
    insert: 'bg-emerald-100 text-emerald-700',
    update: 'bg-amber-100 text-amber-800',
    delete: 'bg-rose-100 text-rose-700',
  } as const;
  return (
    <ul className="divide-y divide-slate-100">
      {modificaciones.map((m) => (
        <li key={m.id} className="py-2">
          <div className="flex items-center justify-between text-xs">
            <span className="font-mono text-slate-600">{fmt.datetime(m.fecha_hora)}</span>
            <span className="inline-flex items-center gap-2">
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${colorBy[m.accion]}`}>
                {m.accion}
              </span>
              <span className="text-slate-500">{m.usuario_email ?? '—'}</span>
            </span>
          </div>
          {m.observaciones && <p className="mt-1 text-xs italic text-slate-700">"{m.observaciones}"</p>}
          <DiffBlock accion={m.accion} cambios={m.cambios} />
        </li>
      ))}
    </ul>
  );
}

function DiffBlock({ accion, cambios }: { accion: 'insert' | 'update' | 'delete'; cambios: Record<string, unknown> }) {
  const entries = useMemo(() => Object.entries(cambios ?? {}), [cambios]);
  if (entries.length === 0) return null;
  if (accion === 'update') {
    return (
      <ul className="mt-1 space-y-0.5 rounded-md bg-slate-50 px-2 py-1 text-[11px] font-mono">
        {entries.map(([k, v]) => {
          const diff = v as { antes?: unknown; despues?: unknown } | null;
          if (diff && typeof diff === 'object' && 'antes' in diff && 'despues' in diff) {
            return (
              <li key={k}>
                <span className="text-slate-500">{k}:</span>{' '}
                <span className="text-rose-600 line-through">{String(diff.antes ?? '—')}</span>{' '}
                → <span className="text-emerald-700">{String(diff.despues ?? '—')}</span>
              </li>
            );
          }
          return <li key={k} className="text-slate-600">{k}: {JSON.stringify(v)}</li>;
        })}
      </ul>
    );
  }
  return (
    <details className="mt-1 text-[11px]">
      <summary className="cursor-pointer text-slate-500">Ver snapshot</summary>
      <pre className="mt-1 overflow-x-auto rounded-md bg-slate-50 px-2 py-1 font-mono">
        {JSON.stringify(cambios, null, 2)}
      </pre>
    </details>
  );
}
